/**
 * Where the tour remembers itself.
 *
 * The account is the source of truth: finishing or skipping is written to the
 * profile (`flashtools_onboarding_tour`), so a new browser or device sees the
 * same answer. `localStorage`, per Firebase uid, is the fallback for the
 * moment before the profile has loaded and for the case where the write
 * fails, so the same device never replays the tour either way.
 */

export type TourOutcome = 'completed' | 'skipped';

const KEY_PREFIX = 'flashtools_tour_v1:';

/** Accounts older than this never auto-start the tour; they can still replay it. */
export const NEW_ACCOUNT_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

function key(uid: string): string {
  return `${KEY_PREFIX}${uid}`;
}

export function readTourOutcome(uid: string): TourOutcome | null {
  try {
    const raw = window.localStorage.getItem(key(uid));
    return raw === 'completed' || raw === 'skipped' ? raw : null;
  } catch {
    return null;
  }
}

export function writeTourOutcome(uid: string, outcome: TourOutcome): void {
  try {
    window.localStorage.setItem(key(uid), outcome);
  } catch {
    // Private mode or blocked storage: the tour simply shows again next time.
  }
}

export function clearTourOutcome(uid: string): void {
  try {
    window.localStorage.removeItem(key(uid));
  } catch {
    // ignore
  }
}

/**
 * Whether an account was created recently enough to count as "just signed
 * up". `creationTime` is Firebase's `user.metadata.creationTime`, an RFC 2822
 * string; an unparseable or missing value is treated as not recent, so an
 * odd token can never spam an old account with the tour.
 */
export function isRecentAccount(creationTime: string | null | undefined, now = Date.now()): boolean {
  if (!creationTime) return false;
  const created = Date.parse(creationTime);
  if (Number.isNaN(created)) return false;
  return now - created >= 0 && now - created <= NEW_ACCOUNT_WINDOW_MS;
}

/** Routes where an auto-started tour would interrupt a payment flow. */
const QUIET_PATH_FRAGMENTS = ['/dice/signup', '/dice/success'];

export interface AutoStartInput {
  uid: string | null | undefined;
  creationTime: string | null | undefined;
  isAdminSession: boolean;
  pathname: string;
  /**
   * What the account itself says, from the profile endpoint. Wins over the
   * browser's memory, so a new device or a cleared browser never replays a
   * tour this account already finished or skipped.
   */
  serverOutcome?: TourOutcome | '' | null;
  now?: number;
}

/**
 * The auto-start rule: a signed-in, non-impersonated, recently created
 * account that has not finished or skipped the tour, on a screen where a
 * tour is not in the way.
 */
export function shouldAutoStartTour(input: AutoStartInput): boolean {
  const { uid, creationTime, isAdminSession, pathname, serverOutcome, now } = input;
  if (!uid || isAdminSession) return false;
  if (QUIET_PATH_FRAGMENTS.some((fragment) => pathname.includes(fragment))) return false;
  if (serverOutcome === 'completed' || serverOutcome === 'skipped') return false;
  if (readTourOutcome(uid)) return false;
  return isRecentAccount(creationTime, now);
}
